import { cn } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { useEffect } from "react";
import { adFormatLabels } from "@/app-config";
import { useMarketsCatalog } from "@/hooks/use-markets-catalog";
import { type CampaignFormValues } from "../campaign-form";
import { MarketStateCard } from "../cards/market-state-card";
import { CampaignContextPill } from "../../shared/campaign-context-pill";
import { FormFieldLabel } from "../../shared/form-field-label";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type MarketsMerchantsStepProps = {
  errorMessage?: string;
  errorField?: string;
  showError?: boolean;
  onDismissError: () => void;
};

export function MarketsMerchantsStep({
  errorMessage,
  errorField,
  showError = false,
  onDismissError,
}: MarketsMerchantsStepProps) {
  const { values, setFieldValue } = useFormikContext<CampaignFormValues>();
  const selectedBrand = values.brand;
  const brandCountry = selectedBrand?.country;
  const isCanadianBrand = brandCountry?.trim().toUpperCase() === "CA";
  const {
    data: states = [],
    isLoading,
    error,
  } = useMarketsCatalog(brandCountry);
  const selectedFormat = adFormatLabels[values.adFormat] ?? "Ad format";

  useEffect(() => {
    if (
      !isLoading &&
      values.marketState &&
      !states.some((state) => state.code === values.marketState)
    ) {
      void setFieldValue("marketState", "");
    }
  }, [isLoading, values.marketState, setFieldValue, states]);

  return (
    <section className="tw:max-w-5xl">
      <StepHeader
        title="Markets & merchants"
        description={`Select the ${isCanadianBrand ? "province" : "state"} you want to run this campaign in, then choose which regions and individual dispensaries are included.`}
      />

      <CampaignContextPill
        brandName={selectedBrand?.name}
        details={[selectedFormat]}
      />

      <StepAlert
        show={showError}
        message={errorMessage}
        onDismiss={onDismissError}
      />

      <div className="tw:mt-8">
        <FormFieldLabel
          className="tw:text-muted-foreground"
          helperText={`Only ${isCanadianBrand ? "provinces" : "states"} where your brand is licensed are available`}
          helperInline
        >
          {isCanadianBrand ? "Province" : "State"}
        </FormFieldLabel>
      </div>

      {isLoading ? (
        <p className="tw:mt-8 tw:text-lg tw:text-muted-foreground">
          Loading markets...
        </p>
      ) : error ? (
        <p className="tw:mt-8 tw:text-lg tw:text-destructive">
          Unable to load markets.
        </p>
      ) : (
        <div
          className={cn(
            "tw:mt-5 tw:grid tw:grid-cols-3 tw:gap-4",
            showError &&
              errorField === "marketState" &&
              "tw:rounded-xl tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3",
          )}
        >
          {states.map((state) => {
            return (
              <MarketStateCard
                key={state.code}
                state={state}
                selected={values.marketState === state.code}
                onSelect={() => setFieldValue("marketState", state.code)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
